import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { ConfigurationTab } from './components/ConfigurationTab';
import { MonitoringTab } from './components/MonitoringTab';
import { RaynetUsersTab } from './components/RaynetUsersTab';
import { ManualEditorTab } from './components/ManualEditorTab';
import { BusinessMaterialsTab } from './components/BusinessMaterialsTab';
import { StatisticsTab } from './components/StatisticsTab';
import { FAQEditTab } from './components/FAQEditTab';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { AdminLoginPage } from './AdminLoginPage';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { Settings, Activity, Users, LogOut, BookOpen, BarChart3, FileText, HelpCircle } from 'lucide-react';

export function AdminDashboard() {
  const { isAdminAuthenticated, logout, isLoading } = useAdminAuth();

  // Zobrazit loading během kontroly autentifikace
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Načítám...</p>
        </div>
      </div>
    );
  }

  // Pokud není přihlášen, zobraz login formulář
  if (!isAdminAuthenticated) {
    return <AdminLoginPage />;
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <img 
                src="/images/blaze-harmony-logo-2025.svg" 
                alt="Blaze Harmony Logo" 
                className="h-12 w-auto"
              />
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  Admin Dashboard
                </h1>
                <p className="text-muted-foreground">
                  Správa Raynet integrace a monitoring formulářů
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <Button
                variant="outline"
                onClick={logout}
                className="flex items-center gap-2"
              >
                <LogOut className="h-4 w-4" />
                Odhlásit se
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="statistics" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 xl:grid-cols-7 gap-1 h-auto py-2 mb-4 sm:mb-0">
            <TabsTrigger value="statistics" className="flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm px-1 sm:px-3 py-2 min-h-[2.5rem] sm:min-h-[2.75rem]">
              <BarChart3 className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Statistiky</span>
              <span className="sm:hidden">Stats</span>
            </TabsTrigger>
            <TabsTrigger value="configuration" className="flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm px-1 sm:px-3 py-2 min-h-[2.5rem] sm:min-h-[2.75rem]">
              <Settings className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Konfigurace</span>
              <span className="sm:hidden">Config</span>
            </TabsTrigger>
            <TabsTrigger value="monitoring" className="flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm px-1 sm:px-3 py-2 min-h-[2.5rem] sm:min-h-[2.75rem]">
              <Activity className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Monitoring</span>
              <span className="sm:hidden">Monitor</span>
            </TabsTrigger>
            <TabsTrigger value="raynet-users" className="flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm px-1 sm:px-3 py-2 min-h-[2.5rem] sm:min-h-[2.75rem]">
              <Users className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Obchodníci</span>
              <span className="sm:hidden">Users</span>
            </TabsTrigger>
            <TabsTrigger value="manual-editor" className="flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm px-1 sm:px-3 py-2 min-h-[2.5rem] sm:min-h-[2.75rem]">
              <BookOpen className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Manuál</span>
              <span className="sm:hidden">Manual</span>
            </TabsTrigger>
            <TabsTrigger value="business-materials" className="flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm px-1 sm:px-3 py-2 min-h-[2.5rem] sm:min-h-[2.75rem]">
              <FileText className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Obchodní materiály</span>
              <span className="sm:hidden">Materials</span>
            </TabsTrigger>
            <TabsTrigger value="faq-edit" className="flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm px-1 sm:px-3 py-2 min-h-[2.5rem] sm:min-h-[2.75rem]">
              <HelpCircle className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">FAQ</span>
              <span className="sm:hidden">FAQ</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="statistics">
            <StatisticsTab />
          </TabsContent>

          <TabsContent value="configuration">
            <ConfigurationTab />
          </TabsContent>

          <TabsContent value="monitoring">
            <MonitoringTab />
          </TabsContent>

          <TabsContent value="raynet-users">
            <RaynetUsersTab />
          </TabsContent>

          <TabsContent value="manual-editor">
            <ManualEditorTab />
          </TabsContent>

          <TabsContent value="business-materials">
            <BusinessMaterialsTab />
          </TabsContent>

          <TabsContent value="faq-edit">
            <FAQEditTab />
          </TabsContent>

        </Tabs>
      </div>
    </div>
  );
} 